import styled, { css } from 'styled-components';

/**
 * One bounded group of the payout form: a tier, or one level's list of
 * provider exceptions. Up to three levels of rules stack vertically in this
 * form, so every group draws its own boundary - the surface is what ties a
 * group's fields and its own "add" button together.
 *
 * The raised variant is the design system's shadow surface (`$shadowDesign` /
 * `Box`): white, hairline border, shadow1. It reads as a card on the cream
 * page and modal backgrounds, where a plain outline barely registers.
 */
export const GroupCard = styled.div<{ $nested?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px 16px;
  border-radius: 20px;
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.mediumGray};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};

  /* Three of these can nest (row > rule > exceptions), and 16px a side each
     leaves a phone almost no room for the fields inside. */
  @media (max-width: 800px) {
    padding: 12px 12px 16px;
  }

  ${({ $nested, theme }) =>
    $nested &&
    css`
      /* A group inside a raised card: a tint instead of a second card, so
         which box sits inside which reads at a glance. */
      border: none;
      box-shadow: none;
      background: ${theme.gray};
    `}
`;

/**
 * Divider between repeated items inside a GroupCard. Separator would drown
 * on the tinted variant, this stays visible on both backgrounds.
 */
export const GroupDivider = styled.div`
  height: 1px;
  flex: none;
  background: ${({ theme }) => `${theme.darkGray}26`};
  margin: 4px 0;
`;

/**
 * The explanation under a section title. A notch above Disclaimer's 12px,
 * which was hard to read in a form this dense, but still clearly secondary to
 * the fields it explains.
 */
export const SectionNote = styled.p<{ $warning?: boolean }>`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme, $warning }) =>
    $warning ? theme.primary : theme.darkGray};

  b {
    font-weight: 700;
  }

  /* A level specific caveat right under the standard explanation. */
  & + & {
    margin-top: 4px;
  }
`;
